<template>
    <BaseModal
        v-if="visible"
        ModalId="modalId"
        :UseScrollable="false"
        :HideModalFun="onHidden"
        :TitleWindow="$t('programSetup.SchedulerTitle')"
        :width="'50%'"
        height="auto"
        :buttons="[
            { text: $t('Shared.BtnCancel'), variant: 'secondary', action: 'Close' },
            { text: $t('Shared.Save'), variant: 'primary', action: emitEventOK }
        ]"
        @Close="close"
    >
        <MdTitle v-bind:Text="program" v-bind:isSubtitle="true" v-bind:Name="'T_TitleSearch'" />
        <b-row>
            <b-col md="4" sm="12" lg="4">
                <MdSelect
                    v-bind:Label="$tc('Shared.day')"
                    v-bind:Options="daysOfWeekOptions"
                    v-bind:Model="d_daysOfWeek"
                    v-bind:FieldDescription="'name'"
                    v-bind:FieldCode="'value'"
                    :Input="changeDropDownDaysOfWeek"
                    v-bind:Placeholder="selectPlaceholder"
                    :isRequired="true"
                    :isValid="isValid('meetingDay')"
                />
            </b-col>
            <b-col md="4" lg="4" sm="12">
                <MdEdit
                    v-bind:Id="'meetingTime'"
                    v-bind:Name="'meetingTime'"
                    v-bind:Label="$t('Shared.Time')"
                    v-bind:TypeProp="'time'"
                    v-model="d_meetingTime"
                    :isRequired="true"
                    :isValid="isValid('meetingTime')"
                    @change="meetingTimeChanged"
                />
            </b-col>
            <b-col md="4" lg="4" sm="12">
                <MdEdit
                    v-bind:Id="'timeTo'"
                    v-bind:Name="'timeTo'"
                    v-bind:Label="$t('Shared.TimeTo')"
                    v-bind:TypeProp="'time'"
                    v-model="d_timeTo"
                    @change="timeToChanged"
                />
            </b-col>
        </b-row>
        <b-row>
            <b-col md="6" sm="12" lg="6">
                <MdSelect
                    v-bind:Label="$tc('Shared.Location')"
                    v-bind:Options="locations"
                    v-bind:Model="d_location"
                    v-bind:FieldDescription="'name'"
                    v-bind:FieldCode="'code'"
                    :Input="changeDropDownLocation"
                    v-bind:Placeholder="selectPlaceholder"
                />
            </b-col>
            <b-col md="6" sm="12" lg="6">
                <MdSelect
                    v-bind:Label="$tc('Shared.Therapist')"
                    v-bind:Options="therapists"
                    v-bind:Model="d_therapist"
                    v-bind:FieldDescription="'name'"
                    v-bind:FieldCode="'code'"
                    :Input="changeDropDownTherapist"
                    v-bind:Placeholder="selectPlaceholder"
                />
            </b-col>
        </b-row>
    </BaseModal>
</template>

<script>
import EnumService from '@/services/enum.service';
import validationMixin from '@/common/validationMixin';
import validationRules from '@/common/validationRules';
import ShowMessage from '@/components/messages/ShowMessage.js';
import Utils from '@/common/utils';

export default {
    name: 'TherapySchedulerModal',
    mixins: [validationMixin, ShowMessage],
    props: {
        visible: {
            type: Boolean,
            required: true
        },
        value: {
            type: Object,
            default: null
        },
        program: {
            type: String,
            required: true
        },
        therapists: {
            type: Array,
            default: () => []
        },
        locations: {
            type: Array,
            default: () => []
        }
    },
    data() {
        const defaultValue = {
            meetingDay: 1,
            meetingTime: null,
            timeTo: null,
            therapistcode: null,
            location: null
        };

        return {
            modalId: 'TherapySchedulerModal',
            emitCloseEvent: false,
            editableItem: { ...(this.value || defaultValue) },
            defaultValue: defaultValue,
            daysOfWeekOptions: EnumService.DaysOfWeek,
            d_daysOfWeek: null,
            selectPlaceholder: this.$t('Shared.SelectOptions'),
            d_therapist: null,
            d_location: null,
            d_meetingTime: null,
            d_timeTo: null
        };
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.$bvModal.show(this.modalId);
                this.emitCloseEvent = true;
            }
        },
        value(newValue) {
            this.editableItem = { ...(newValue || this.defaultValue) };
            this.d_daysOfWeek = this.daysOfWeekOptions.find(
                x => x.value === String(this.editableItem.meetingDay)
            );
            this.d_therapist = this.therapists.find(
                x => x.code === this.editableItem.therapistcode
            );
            this.d_location = this.locations.find(x => x.code === this.editableItem.location);
            this.d_meetingTime = Utils.formatTimeHm(this.editableItem.meetingTime) ?? null;
            this.d_timeTo = Utils.formatTimeHm(this.editableItem.timeTo) ?? null;
        }
    },
    methods: {
        resetData() {
            this.editableItem = { ...this.defaultValue };
            this.d_daysOfWeek = this.daysOfWeekOptions.find(
                x => x.value === String(this.editableItem.meetingDay)
            );
            this.d_therapist = null;
            this.d_location = null;
            this.d_meetingTime = null;
            this.d_timeTo = null;
        },

        emitEventCancel() {
            this.emitCloseEvent = false;
            this.resetData();

            this.$emit('update:visible', false);
        },

        emitEventOK(bvModalEvent) {
            if (this.validate_required()) {
                this.$emit('update:visible', false);
                this.$emit('ok', { ...this.editableItem, touched: true });
                this.resetData();
            } else {
                bvModalEvent.preventDefault();

                this.showMessage(
                    this.$tc('Msg.Error'),
                    this.$t('Msg.CheckRedLabeledFields'),
                    'errorAutoHide'
                );
            }
        },

        onHidden() {
            if (this.emitCloseEvent) {
                this.$emit('update:visible', false);
                this.emitCloseEvent = false;
                this.resetData();
            }
        },

        close() {
            this.emitEventCancel();
        },

        validate_required() {
            const rules = {
                meetingDay: [validationRules.required(this.$t('Shared.RequiredField'))],
                meetingTime: [validationRules.required(this.$t('Shared.RequiredField'))]
            };

            return this.validateFields(rules, this.editableItem);
        },

        changeDropDownDaysOfWeek(value) {
            this.editableItem.meetingDay = value ? +value.value : null;
        },

        changeDropDownLocation(value) {
            this.editableItem.location = value ? value.code : null;
        },

        changeDropDownTherapist(value) {
            this.editableItem.therapistcode = value ? value.code : null;
        },

        meetingTimeChanged(value) {
            this.editableItem.meetingTime = value ? Utils.formatterShortTimeToIso(value) : null;
        },

        timeToChanged(value) {
            this.editableItem.timeTo = value ? Utils.formatterShortTimeToIso(value) : null;
        }
    }
};
</script>
